import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as moment from 'moment';
import * as _ from 'lodash';

import {
	JobShiftState,
	jobShiftsFeatureKey,
	selectAll,
	selectIds,
	JobShiftStoreModel,
	ShiftByWeekday,
	ShiftByStartTime,
} from './job-shift.reducer';
import { JobShift, Job, ServiceType } from 'src/shared/api';
import {
	RwicShifts,
	RwicShiftsByWeekday,
	AssigmentSummary,
	AssignmentsByWeekday,
} from 'src/shared/models/job-shift-needs';
import { fieldWorkerToLink } from 'src/shared/utilities/utility-functions';

export const selectJobShiftState =
	createFeatureSelector<JobShiftState>(jobShiftsFeatureKey);

export const selectJobShiftLoading = createSelector(
	selectJobShiftState,
	(jobShiftState) => jobShiftState.loading
);

export const selectCurrentJobShiftWeekStartDate = createSelector(
	selectJobShiftState,
	(jobShiftState) => jobShiftState.startDate
);

export const selectJobShiftToDelete = createSelector(
	selectJobShiftState,
	(jobShiftState) => jobShiftState.shiftsToDelete
);

export const selectJobShiftIdsToDelete = createSelector(
	selectJobShiftState,
	(jobShiftState) => jobShiftState.shiftIdsToDelete
);

export const selectAllJobShifts = createSelector(
	selectJobShiftState,
	selectAll
);

export const getJobShiftIds = createSelector(selectJobShiftState, selectIds);
export const getNewJobShiftId = createSelector(
	getJobShiftIds,
	(jobShiftIds) => {
		const min = Math.min(...(jobShiftIds as number[]));
		return min > 0 ? -1 : min - 1;
	}
);

export const selectJobShiftById = (jobShiftId: number) =>
	createSelector(
		selectJobShiftState,
		(jobShiftState) => jobShiftState.entities[jobShiftId]
	);

export const selectJobShiftsByJob = (jobId: number | string) =>
	createSelector(selectAllJobShifts, (jobShiftState) =>
		jobShiftState.filter((js) => js.job.id === ~~jobId)
	);

export const selectJobShiftToDeleteForJob = (jobId: number | string) =>
	createSelector(selectJobShiftToDelete, (jobShiftToDelete) =>
		jobShiftToDelete.filter((js) => js.job?.id === ~~jobId)
	);

export const selectDirtyJobShifts = () =>
	createSelector(selectAllJobShifts, (jobShifts) =>
		jobShifts.filter((js) => js.dirty)
	);

export const selectBulkAssignShiftsResponse = createSelector(
	selectJobShiftState,
	(jobShiftState) => jobShiftState.bulkAssignShiftsResponse
);

export const selectDirtyJobShiftsForJob = (jobId: number | string) =>
	createSelector(selectJobShiftsByJob(jobId), (dirtyJobShifts) => {
		return dirtyJobShifts.filter((js) => js.dirty);
	});

export const selectDirtyJobShiftsByRwic = (jobId: number | string) =>
	createSelector(selectJobShiftsByJob(jobId), (jobShifts) => {
		const results = _(jobShifts)
			.filter((js) => js.dirty)
			.filter((js) => js.rwic !== null)
			.groupBy((js) => `${js.startTime}--${js.serviceType.description}`)
			.map((assignments, serviceStartTime) => {
				const [startTime, serviceType] = serviceStartTime.split('--');
				const assigmentSummaries = _(assignments)
					.groupBy((js) => js.rwic && js.rwic.id)
					.map((summaries) => {
						const rwic = summaries[0].rwic;
						const dates = _(summaries)
							.map((s) => s.day)
							.value();
						const summaryByTime = {
							rwic: rwic,
							dates,
							shifts: summaries,
						};
						return summaryByTime;
					})
					.value();
				return {
					serviceType,
					startTime:
						startTime === 'null'
							? null
							: moment.utc(startTime, 'HH:mm:ss'),
					summaries: assigmentSummaries,
				};
			})
			.orderBy((x) => x.startTime && x.serviceType)
			.value();
		return results;
	});

export const selectJobShiftsByJobToBeAdded = (jobId: number | string) =>
	createSelector(selectAllJobShifts, (jobShifts) =>
		_(jobShifts)
			.filter((js) => js.id < 0 && js.job?.id === ~~jobId)
			.groupBy((js) => `${js.startTime}--${js.serviceType.description}`)
			.map((shiftsByTime, serviceStartTime) => {
				const [startTime, serviceType] = serviceStartTime.split('--');
				const shiftsDay = _(shiftsByTime)
					.groupBy('day')
					.map((shifts, day) => {
						return {
							day,
							shifts,
						};
					})
					.value();
				return {
					serviceType,
					startTime: moment.utc(startTime, 'HH:mm:ss'),
					additions: shiftsDay,
				};
			})
			.value()
	);

export const selectJobShiftsByJobToBeRemoved = (jobId: number | string) =>
	createSelector(selectJobShiftToDeleteForJob(jobId), (jobShiftToDelete) =>
		_(jobShiftToDelete)
			.groupBy((js) => `${js.startTime}--${js.serviceType.description}`)
			.map((shiftsByTime, serviceStartTime) => {
				const [startTime, serviceType] = serviceStartTime.split('--');
				const shiftsDay = _(shiftsByTime)
					.groupBy('day')
					.map((shifts, day) => {
						return {
							day,
							shifts,
						};
					})
					.value();
				return {
					serviceType,
					startTime: moment.utc(startTime, 'HH:mm:ss'),
					deletions: shiftsDay,
				};
			})
			.value()
	);

export const selectJobShiftAddAndRemoveByJobAndTime = (
	jobId: number | string
) =>
	createSelector(
		selectJobShiftsByJobToBeAdded(jobId),
		selectJobShiftsByJobToBeRemoved(jobId),
		(additions, deletions) => _.merge([], additions, deletions)
	);

export const selectDirtyJobShiftsByRwicToBeRemoved = (jobId: number | string) =>
	createSelector(selectJobShiftsByJob(jobId), (jobShifts) =>
		_(jobShifts)
			.filter((js) => js.dirty)
			.filter((js) => js.rwic === null && js.originalRwic !== null)
			.groupBy((js) => `${js.startTime}--${js.serviceType.description}`)
			.map((assignments, serviceStartTime) => {
				const [startTime, serviceType] = serviceStartTime.split('--');
				const assigmentSummaries = _(assignments)
					.groupBy((js) => js.originalRwic && js.originalRwic.id)
					.map((summaries): AssigmentSummary => {
						const rwic = summaries[0].originalRwic;
						const dates = _(summaries)
							.map((s) => moment.utc(s.day))
							.value();
						const summaryByTime = {
							rwic: fieldWorkerToLink(rwic),
							dates,
							shifts: summaries,
						};
						return summaryByTime;
					})
					.value();

				return {
					serviceType,
					startTime: moment.utc(startTime, 'HH:mm:ss'),
					summaries: assigmentSummaries,
				};
			})
			.orderBy((x) => x.startTime.format('HH:mm:ss') && x.serviceType)
			.value()
	);

export const selectJobShiftsByDate = (date: moment.Moment) =>
	createSelector(selectAllJobShifts, (jobShiftState) =>
		jobShiftState.filter((js) => moment.utc(date).isSame(js.day))
	);

export const selectJobShiftByJobDateServiceAndStartTime = (
	jobId: number,
	startTime: moment.Moment,
	serviceType: ServiceType,
	shiftDate?: moment.Moment
) =>
	createSelector(selectJobShiftsByJob(jobId), (jobShifts) =>
		jobShifts.filter((js) => {
			// if shiftDate null return all dates
			const sameDate = shiftDate ? shiftDate.isSame(js.day, 'd') : true;
			const sameTime =
				moment.utc(startTime).format('HH:mm:ss') ===
				moment.utc(js.startTime, 'HH:mm:ss').format('HH:mm:ss');
			return sameDate && sameTime && js.serviceType.id === serviceType.id;
		})
	);

export const selectJobShiftsForJobWeek = (
	jobId: number | string,
	startDate: moment.Moment
) =>
	createSelector(selectJobShiftsByJob(jobId), (jobShiftState) =>
		jobShiftState.filter((js) => {
			const sd = moment.utc(startDate).startOf('w');
			const ed = moment.utc(startDate).endOf('w');
			return moment.utc(js.day).isBetween(sd, ed, 'day', '[]');
		})
	);

const scheduledForOtherJobsAllWeek = (
	jobId: number,
	shifts: JobShiftStoreModel[],
	sd: moment.Moment,
	ed: moment.Moment
) => {
	return _(shifts)
		.reduce((week, shift) => {
			const day = moment.utc(shift.day);
			if (day.isBetween(sd, ed, 'd', '[]') && shift.job.id !== jobId) {
				week[day.weekday()] = true;
			}
			return week;
		}, new Array(7).fill(false))
		.every((wd) => wd);
};

export const selectRwicJobShiftsForWeek = (
	startDate: moment.Moment,
	thisJobId?: number
) =>
	createSelector(selectAllJobShifts, (jobShiftState) => {
		const sd = moment.utc(startDate).startOf('w');
		const ed = moment.utc(startDate).endOf('w');
		return _(jobShiftState)
			.filter((js) => {
				return (
					js.rwic && moment.utc(js.day).isBetween(sd, ed, 'day', '[]')
				);
			})
			.groupBy((shiftInWeek: JobShiftStoreModel) => {
				return shiftInWeek.rwic && shiftInWeek.rwic.id;
			})
			.map((rwicShifts, rwic): RwicShifts => {
				return {
					rwicId: ~~rwic,
					shifts: rwicShifts,
				};
			})
			.reduce(
				(
					rwicShifts: RwicShiftsByWeekday[],
					assignedShifts: RwicShifts,
					key
				) => {
					rwicShifts[key] = {
						...rwicShifts[key],
						rwicId: assignedShifts.rwicId,
					};

					rwicShifts[key].scheduledForOtherJobsAllWeek =
						scheduledForOtherJobsAllWeek(
							~~thisJobId,
							assignedShifts.shifts,
							sd,
							ed
						);

					rwicShifts[key].assignmentsByWeekday = _(
						assignedShifts.shifts
					)
						.groupBy((s: any) => {
							return moment.utc(s.day).weekday();
						})
						.reduce(
							(
								assignmentsByWeekdays: AssignmentsByWeekday[],
								shifts: JobShiftStoreModel[],
								weekdayNumber: string
							) => {
								assignmentsByWeekdays[~~weekdayNumber] = <
									AssignmentsByWeekday
								>{
									...assignmentsByWeekdays[~~weekdayNumber],
									weekday: ~~weekdayNumber,
									assignments: shifts,
									firstOtherJob: _.chain(shifts)
										.find(
											(s) => s && s.job.id !== ~~thisJobId
										)
										.get('job')
										.value(),
									assignedToOtherJob: _.some(
										shifts,
										(s) => s.job.id !== ~~thisJobId
									),
									hasShiftForThisJob: _.some(
										shifts,
										(s) => s.job.id === ~~thisJobId
									),
									hasDirtyShift: _.some(
										shifts,
										(s) => s.dirty
									),
								};
								return assignmentsByWeekdays;
							},
							new Array(7).fill(null)
						);

					return rwicShifts;
				},
				[]
			);
	});

export const selectJobShiftsForWeekGroupedForCalendar = (
	job: Job,
	startDate: moment.Moment
) =>
	createSelector(
		selectJobShiftsForJobWeek(job.id, startDate),
		selectJobShiftIdsToDelete,
		(jobShiftForWeek, toBeDeleted) => {
			const results = _(jobShiftForWeek)
				.differenceWith(toBeDeleted, (js, delId) => {
					return js.id === delId;
				})
				.groupBy((s) => `${s.serviceType.description}--${s.startTime}`)
				.map((hourShifts, startTime) => {
					return {
						id: hourShifts[0].id,
						serviceType: hourShifts[0].serviceType,
						startTime:
							startTime.split('--')[1] !== 'null'
								? moment
										.utc(startTime, 'HH:mm:ss')
										.format('HH:mm:ss')
								: null,
						hourShifts,
					};
				})
				.reduce((shiftsByStartTime: ShiftByStartTime[], value, key) => {
					const weekdays = new Array(7)
						.fill({})
						.map((s: ShiftByWeekday, i) => {
							const date = moment.utc(startDate).add(i, 'day');
							s.weekday = i;
							s.continuesBefore =
								date.isAfter(job.start, 'day') && i === 0;
							s.continuesAfter =
								date.isBefore(job.end, 'day') && i === 6;
							s.isStartOfJob = date.isSame(job.start, 'day');
							s.isEndOfJob = date.isSame(job.end, 'day');
							s.isDuringJob = date.isBetween(
								job.start,
								job.end,
								'day',
								'[]'
							);
							return { ...s };
						});

					shiftsByStartTime[key] = shiftsByStartTime[key] || {
						id: value.id,
						serviceType: value.serviceType,
						startTime: value.startTime
							? moment.utc(value.startTime, 'HH:mm:ss')
							: null,
					};

					const shiftsByWeekday: ShiftByWeekday[] = _(
						value.hourShifts
					)
						.groupBy((s) => {
							return moment.utc(s.day).weekday();
						})
						.reduce((acc: any, curr: any, index: string) => {
							acc[~~index] =
								acc[~~index].day ||
								({
									weekday: ~~index,
									shiftUnAssignedCount: 0,
									shiftsNeeded: curr,
									continuesBefore:
										acc[~~index].continuesBefore,
									continuesAfter: acc[~~index].continuesAfter,
									isDuringJob: acc[~~index].isDuringJob,
									isStartOfJob: acc[~~index].isStartOfJob,
									isEndOfJob: acc[~~index].isEndOfJob,
								} as ShiftByWeekday);
							acc[~~index].shiftUnAssignedCount = _(
								acc[~~index].shiftsNeeded
							).sumBy((s: JobShift) => (s.rwic ? 1 : 0));
							acc[~~index].needsAssignments = _(
								acc[~~index].shiftsNeeded
							).some((s: JobShift) => s.rwic === null);
							acc[~~index].allAssigned = _(
								acc[~~index].shiftsNeeded
							).every((s: JobShift) => !!s.rwic);
							return acc;
						}, weekdays);

					shiftsByStartTime[key].hourShifts = shiftsByWeekday as any;

					return shiftsByStartTime;
				}, []);
			return _.orderBy(results, (x) =>
				moment.utc(x.startTime).format('HH:mm:ss')
			);
		}
	);

export const selectAnyJobShiftChanges = (jobId: number | string) =>
	createSelector(
		selectDirtyJobShiftsForJob(jobId),
		selectJobShiftToDeleteForJob(jobId),
		(dirtyJobs, toBeRemoved) => {
			return ~~dirtyJobs.length + ~~toBeRemoved.length > 0;
		}
	);
